import { axiosInstanceNoToast } from '../fetchConfig/axiosConfig.js';
import { usePersistedDataStore } from '../components/store.js';
import { mergeAndDownload, downloadIndividualPdfs, generateWorkupFileName } from '../components/CaseReview/Workups/WorkupDownload.js';
import { mapRuntype } from '../utils/mapRuntype.js';
import { getWorkupReportLabel, getWorkupS3FileName, EVIDENCE_TYPE_TO_REGEN_OPTIONS, EVIDENCE_TYPES_FOR_SELECTION } from '../common/mappings.js';
import { toast } from '../common/toast';
import logger from '../utils/logger';

export { EVIDENCE_TYPE_TO_REGEN_OPTIONS, EVIDENCE_TYPES_FOR_SELECTION };

/**
 * Checks if user is a training user
 * @param {Object} negotiationObj - Negotiation object
 * @returns {boolean} Whether user is a training user
 */
const isTrainingUser = (negotiationObj) => {
  return negotiationObj?.TrainingUser === 1 || 
         (typeof window !== 'undefined' && localStorage.getItem('userInfo')?.includes('"level":"Training"'));
};

/**
 * Maps report type to API format using negotiation context
 * @returns {string} API report type
 */
const getApiReportType = () => {
  const { negotiationObj } = usePersistedDataStore.getState();
  const reportType = negotiationObj?.reportType || 'standard';
  const training = isTrainingUser(negotiationObj);
  
  if (training) return 'training';
  if (reportType === 'village' || negotiationObj?.villageRun === 1) return 'village';
  return mapRuntype(reportType).toLowerCase();
};

/**
 * Gets tax year from negotiation context
 * @returns {number} Tax year
 */
const getTaxYear = () => {
  const { negotiationObj } = usePersistedDataStore.getState();
  const year = negotiationObj?.TaxYear ? parseInt(negotiationObj.TaxYear) : null;
  if (!year) {
    throw new Error('TaxYear is required but not found in negotiationObj');
  }
  return year;
};

/**
 * Builds API payload for generating comp reports using context
 * @param {Array<string>} parcelIds - Array of parcel IDs
 * @param {string} typeOutput - Type of output (e.g., 'onepagermaps', 'fnmaSummary')
 * @param {string} [reportTypeOverride] - Optional; when set, used instead of getApiReportType() (e.g. 'standard', 'assessment')
 * @returns {Object} API payload
 */
const buildApiPayload = (parcelIds, typeOutput, reportTypeOverride, userId) => {
  const apiReportType = reportTypeOverride == null ? getApiReportType() : mapRuntype(reportTypeOverride).toLowerCase();
  const apiOptions = {
    typeOutput: typeOutput,
    useIFMV: false,
    useWeighted: true,
    useRegridOnly: false,
    findBestComp: false,
    reportType: apiReportType,
    showNotes: false
  };

  return {
    subjectParcelId: parcelIds,
    year: getTaxYear(),
    userId,
    options: apiOptions,
    TownWorkup: false
  };
};

/**
 * Processes a single batch with a given typeOutput
 * @param {Array<string>} batch - Array of parcel IDs in this batch
 * @param {number} batchIndex - Index of the batch
 * @param {string} typeOutput - Type of output
 * @param {Function} onParcelComplete - Callback for parcel completion (parcelId, typeOutput)
 * @param {string} [reportTypeOverride] - Optional; passed to buildApiPayload when set
 * @returns {Promise<Object>} Result of the batch processing
 */
const processBatch = async (batch, batchIndex, typeOutput, onParcelComplete, reportTypeOverride, userId) => {
  try {
    const apiPayload = buildApiPayload(batch, typeOutput, reportTypeOverride, userId);
    logger.log('[regenerateWorkups] generate_comp_report payload', {
      batchIndex,
      typeOutput,
      reportTypeOverride: reportTypeOverride ?? null,
      payload: {
        ...apiPayload,
        subjectParcelId: Array.isArray(apiPayload?.subjectParcelId) ? apiPayload.subjectParcelId.slice(0, 25) : apiPayload?.subjectParcelId,
        subjectParcelIdCount: Array.isArray(apiPayload?.subjectParcelId) ? apiPayload.subjectParcelId.length : undefined,
      },
    });
    const response = await axiosInstanceNoToast.post('/generate_comp_report', {
      reportData: apiPayload
    });

    // Notify completion for each parcel with the report type
    // The caller will track which report types are complete per parcel
    if (onParcelComplete) {
      batch.forEach((parcelId) => {
        onParcelComplete(parcelId, typeOutput);
      });
    }

    if (!response.data.success) {
      const errMsg = response.data?.error || response.data?.message || 'Batch failed';
      logger.error(`Batch ${batchIndex + 1} (${typeOutput}) failed:`, errMsg);
      return { success: false, batchIndex, typeOutput, message: errMsg };
    }

    return { success: true, batchIndex, typeOutput };
  } catch (error) {
    const errMsg = error?.response?.data?.error || error?.message || String(error);
    logger.error(`Error processing batch ${batchIndex + 1} (${typeOutput}):`, errMsg);
    if (onParcelComplete) {
      batch.forEach(parcelId => onParcelComplete(parcelId, typeOutput));
    }
    return { success: false, batchIndex, typeOutput, message: errMsg };
  }
};

/**
 * Service for regenerating workups
 * @param {Object} options - Configuration options
 * @param {Array<string>} options.parcelIds - Array of parcel IDs to regenerate
 * @param {Array<string>} options.reportTypes - Array of report types to generate (e.g., ['onepagermaps', 'fnmaSummary'])
 * @param {string} [options.reportType] - Optional; when set, used for API reportType instead of getApiReportType() (e.g. 'standard', 'assessment')
 * @param {Function} options.onParcelComplete - Callback function called when a parcel completes
 * @param {string} options.fileName - Optional filename for downloads
 * @param {boolean} options.download - Whether to download workups after regeneration (default: false)
 * @param {number} options.userId - User ID from useUser()
 * @param {boolean} options.combinePdfs - When downloading, merge into one PDF (default: true)
 * @returns {Promise<Object>} Result of regeneration
 */
export const regenerateWorkups = async (options = {}) => {
  const {
    parcelIds,
    reportTypes = ['onepagermaps'],
    reportType: reportTypeOverride,
    userId,
    onParcelComplete,
    fileName,
    download = false,
    combinePdfs = true,
    trackRuntime = false,
    suppressToasts = false,
  } = options;

  if (userId == null) {
    throw new Error('userId is required for workup regeneration');
  }

  logger.log('[regenerateWorkups] Starting regeneration', {
    parcelCount: parcelIds?.length ?? 0,
    reportTypes,
    reportTypeOverride,
  });

  if (trackRuntime) {
    logger.log('regenerateWorkups options INVOKED', options);
  }
  const overallStart = trackRuntime ? Date.now() : 0;
  
  if (!parcelIds || parcelIds.length === 0) {
    logger.warn('No parcel IDs provided for workup regeneration');
    return { success: false, message: 'No parcel IDs provided' };
  }

  if (!reportTypes || reportTypes.length === 0) {
    logger.warn('No report types provided for workup regeneration');
    return { success: false, message: 'No report types provided' };
  }

  if (trackRuntime) {
    logger.log('Regenerate Workups triggered', { parcelIds, count: parcelIds.length, reportTypes });
  }

  // Validate tax year exists
  getTaxYear();

  // Batch parcels into groups
  const batchSize = 50
  const batches = [];
  for (let i = 0; i < parcelIds.length; i += batchSize) {
    batches.push(parcelIds.slice(i, i + batchSize));
  }

  if (trackRuntime) {
    logger.log(`Processing ${parcelIds.length} parcels in ${batches.length} batch(es) with report types: ${reportTypes.join(', ')}`);
  }

  // Track completion per parcel per report type
  // Only mark parcel as complete when ALL report types are done
  const parcelCompletionTracker = new Map();
  parcelIds.forEach(parcelId => {
    parcelCompletionTracker.set(parcelId, new Set());
  });

  // Create a wrapper for onParcelComplete that tracks completion per report type
  const trackedOnParcelComplete = (parcelId, typeOutput) => {
    const completedTypes = parcelCompletionTracker.get(parcelId);
    if (completedTypes) {
      completedTypes.add(typeOutput);
      
      // Only call the original callback when ALL report types are complete for this parcel
      if (completedTypes.size === reportTypes.length) {
        if (onParcelComplete) {
          onParcelComplete(parcelId);
        }
      }
    }
  };

  const parallelPromises = [];
  // Process all batches for all report types fully in parallel
  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    const batch = batches[batchIndex];
    for (const typeOutput of reportTypes) {
      parallelPromises.push(
        processBatch(batch, batchIndex, typeOutput, trackedOnParcelComplete, reportTypeOverride, userId)
      );
    }
  }

  const parallelStart = parallelPromises.length > 0 && trackRuntime ? Date.now() : 0;
  const parallelResults = parallelPromises.length > 0 ? await Promise.all(parallelPromises) : [];
  const parallelDurationMs = parallelPromises.length > 0 && parallelStart ? Date.now() - parallelStart : 0;
  if (trackRuntime && parallelPromises.length > 0) {
    logger.log(
      '[regenerateWorkups] Parallel batches duration:',
      `${(parallelDurationMs / 1000).toFixed(2)}s with ${parallelPromises.length} promise(s)`
    );
  }
  let errors = parallelResults
    .filter((r) => !r?.success)
    .map((r) => ({
      message: r?.message || 'Workup generation failed',
      batchIndex: r?.batchIndex,
      typeOutput: r?.typeOutput
    }));

  if (trackRuntime && errors.length > 0) {
    logger.warn('[regenerateWorkups] Batch generation completed with errors', {
      errorCount: errors.length,
      reportTypes
    });
  }

  // Toasts are shown after S3Files update so any S3 failures are included in the result.

  // Update S3Files in negotiationObj for regenerated parcels
  try {
    logger.log('beginning s3 file update')
    await updateS3FilesInNegotiationObj(parcelIds);
  } catch (error) {
    logger.error('Error updating S3Files in negotiation object:', error);
    errors = [
      ...errors,
      {
        message: error?.response?.data?.message || error?.message || 'Failed to update S3Files in negotiation object',
        batchIndex: null,
        typeOutput: 's3_files_update',
      },
    ];
  }

  // Show toast again after S3Files update so 401/S3 failures prevent success messaging.
  if (!suppressToasts && errors.length > 0) {
    const msgCounts = errors.reduce((acc, e) => {
      const msg = e?.message || 'Workup generation failed';
      acc[msg] = (acc[msg] || 0) + 1;
      return acc;
    }, {});

    const msgSummary = Object.entries(msgCounts)
      .slice(0, 4)
      .map(([msg, count]) => (count > 1 ? `${msg} (x${count})` : msg))
      .join('\n');

    const reportTypesText = reportTypes.length === 1 ? getWorkupReportLabel(reportTypes[0]) : reportTypes.map(getWorkupReportLabel).join(' and ');
    toast.error([
      `Workup regeneration completed with ${errors.length} error(s)`,
      `${reportTypesText} (parcels: ${parcelIds.length})`,
      msgSummary || null,
    ].filter(Boolean).join('\n'), { autoClose: false });
  } else if (!suppressToasts && !download && errors.length === 0) {
    const reportTypesText = reportTypes.length === 1 ? getWorkupReportLabel(reportTypes[0]) : reportTypes.map(getWorkupReportLabel).join(' and ');
    toast.success(`All ${reportTypesText} workups regenerated`, { autoClose: 5000 });
  }

  // If download is requested, download the workups from S3
  if (download) {
    try {

      const { negotiationObj } = usePersistedDataStore.getState();
      const taxYear = negotiationObj?.TaxYear ? parseInt(negotiationObj.TaxYear) : parseInt(String(new Date().getFullYear()));
      const reportType = reportTypeOverride ?? negotiationObj?.reportType ?? 'standard';
      const trainingDownload = reportTypeOverride == null && isTrainingUser(negotiationObj);

      logger.log('reportTypes coming in as ', reportTypes);
      for (const typeOutput of reportTypes) {
        const generatedName = getWorkupS3FileName(reportType, typeOutput);
        // Training generation uploads training_comps*.pdf. Offers, email, and the viewer keep the sales/equity name.
        const suffix = trainingDownload
          ? generatedName.replace(/^(sales_comps|equity_comps|village_comps)/, 'training_comps')
          : generatedName;
        const expectedFilename = `${suffix}.pdf`;
        logger.log('[regenerateWorkups] download selection', {
          reportType,
          reportTypeOverride,
          typeOutput,
          suffix,
          expectedFilename,
          taxYear,
          parcelCount: parcelIds.length,
        });
        
        // Generate filename internally (fileName param kept for backward compatibility)
        const defaultFileName = fileName || generateWorkupFileName(parcelIds, negotiationObj, reportType, typeOutput);
        // Fetch presigned URLs and download

        logger.log('expected filename constructed from suffix and type output', expectedFilename);
        let parcelUrlPairs = [];
        try {
          const response = await axiosInstanceNoToast.post('/get_s3_files_batch', {
            parcel_ids: parcelIds,
            tax_year: taxYear,
            bucket_name: 'aventine-court-docs'
          });

          if (response?.data?.success && response?.data?.s3_files) {
            parcelUrlPairs = parcelIds
              .map((parcelId) => {
                const s3Files = response.data.s3_files[parcelId] || [];
                // Exact match only — stem includes (e.g. sales_comps) would also hit sales_comps_nomaps.pdf
                const matchingFile = s3Files.find((file) => {
                  const filename = file?.Filename || file?.Key?.split('/').pop() || '';
                  return filename === expectedFilename;
                });

                return matchingFile?.Url ? { parcelId, url: matchingFile.Url } : null;
              })
              .filter(Boolean);
          }
        } catch (error) {
          parcelUrlPairs = [];
        }

        const missingFromS3 = parcelIds.filter(
          (parcelId) => !parcelUrlPairs.some((pair) => pair.parcelId === parcelId)
        );
        if (missingFromS3.length > 0) {
          logger.warn('[regenerateWorkups] parcels missing S3 workup file', {
            expectedFilename,
            requestedCount: parcelIds.length,
            foundCount: parcelUrlPairs.length,
            missingCount: missingFromS3.length,
            missingFromS3,
          });
          if (!suppressToasts) {
            toast.warning(
              `${missingFromS3.length} of ${parcelIds.length} parcels have no ${expectedFilename} in S3 and will be skipped.`,
              { autoClose: 8000 }
            );
          }
        }

        logger.log('urls before merge and download', parcelUrlPairs);
        logger.log('[regenerateWorkups] urls fetched for merge', {
          expectedFilename,
          urlsCount: parcelUrlPairs.length,
          fileName: defaultFileName,
          combinePdfs,
        });
        
        if (parcelUrlPairs.length === 0) {
          logger.warn(`No workup files found for download: ${expectedFilename}`);
          continue;
        }
        
        if (combinePdfs) {
          await mergeAndDownload(
            parcelUrlPairs.map(({ url }) => url),
            { fileName: defaultFileName }
          );
        } else {
          await downloadIndividualPdfs(parcelUrlPairs, { zipFileName: defaultFileName });
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      logger.error('Error downloading workups after regeneration:', error);
      if (!suppressToasts) {
        toast.error(error?.message || 'Error downloading workups.');
      }
      throw error;
    }
  }

  if (trackRuntime && overallStart) {
    const totalDurationMs = Date.now() - overallStart;
    logger.log('[regenerateWorkups] Total duration:', `${(totalDurationMs / 1000).toFixed(2)}s`, {
      parallelMs: parallelDurationMs,
      parcelCount: parcelIds.length,
      batchCount: batches.length,
      reportTypes
    });
  }

  return {
    // Always mark as "completed" so callers can continue with partial results.
    // Consumers should use `errors` to surface failures.
    success: true,
    message: errors.length === 0
      ? `Workup regeneration completed for ${parcelIds.length} parcels in ${batches.length} batch(es)`
      : `Workup regeneration completed for ${parcelIds.length} parcels in ${batches.length} batch(es) with errors`,
    errors
  };
};

/**
 * Parallel promise wrapper: invokes regenerateWorkups once per evidence type (e.g. evidence_comparison flow).
 * @param {Object} options
 * @param {Array<string>} options.parcelIds - Parcel IDs to regenerate
 * @param {Array<string>} options.evidenceTypes - Evidence types (e.g. ['Unequal', 'Excessive'])
 * @param {number} options.userId - User ID from useUser()
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export const regenerateWorkupsByEvidenceTypes = async ({ parcelIds, evidenceTypes, userId }) => {
  if (userId == null) {
    throw new Error('userId is required for workup regeneration');
  }
  if (!evidenceTypes?.length) {
    return { success: false, message: 'No evidence types provided' };
  }
  const optionsList = evidenceTypes.map((t) => EVIDENCE_TYPE_TO_REGEN_OPTIONS[t]).filter(Boolean);
  if (optionsList.length !== evidenceTypes.length) {
    return { success: false, message: 'Invalid evidence type(s)' };
  }
  const { negotiationObj } = usePersistedDataStore.getState();
  const isVillageContext = negotiationObj?.reportType === 'village' || negotiationObj?.villageRun === 1;
  logger.log('[regenerateWorkupsByEvidenceTypes] Invoking regeneration for evidence types', {
    evidenceTypes,
    optionsList,
    parcelCount: parcelIds?.length ?? 0,
    isVillageContext,
    negotiationReportType: negotiationObj?.reportType ?? null,
  });

  const results = await Promise.all(
    optionsList.map((opts) =>
      regenerateWorkups({
        parcelIds,
        userId,
        ...opts,
        // Evidence-type mappings default to standard/assessment, but village runs must generate village workups.
        ...(isVillageContext ? { reportType: 'village' } : {}),
        // Avoid N toasts; this wrapper owns the single aggregated toast.
        suppressToasts: true
      })
    )
  );
  logger.log('results from regenerateWorkupsByEvidenceTypes', results);

  const errors = results.flatMap((result, idx) => {
    const evidenceType = evidenceTypes[idx];
    const opts = optionsList[idx];
    const innerErrors = Array.isArray(result?.errors) ? result.errors : [];
    if (!innerErrors.length) return [];

    return innerErrors.map((e) => ({
      ...e,
      evidenceType,
      evidenceLabel: opts?.label
    }));
  });

  if (errors.length > 0) {
    const msgCounts = errors.reduce((acc, e) => {
      const msg = e?.message || 'Workup generation failed';
      acc[msg] = (acc[msg] || 0) + 1;
      return acc;
    }, {});

    const msgSummary = Object.entries(msgCounts)
      .slice(0, 4)
      .map(([msg, count]) => (count > 1 ? `${msg} (x${count})` : msg))
      .join('\n');

    toast.error([
      `Workup regeneration completed with ${errors.length} error(s)`,
      `Evidence types: ${evidenceTypes.join(', ')}`,
      msgSummary || null,
    ].filter(Boolean).join('\n'), { autoClose: false });
  } else {
    toast.success(`All selected workups regenerated`, { autoClose: 5000 });
  }

  return {
    success: true,
    message: errors.length === 0
      ? 'All workups regenerated'
      : `Workup regeneration completed with ${errors.length} error(s)`,
    errors
  };
};

/**
 * Fetches S3Files for parcels and updates negotiationObj
 * @param {Array<string>} parcelIds - Array of parcel IDs to fetch S3Files for
 */
const updateS3FilesInNegotiationObj = async (parcelIds) => {
  const { negotiationObj } = usePersistedDataStore.getState();
  const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
  const setNegotiationObjVersion = usePersistedDataStore.getState().setNegotiationObjVersion;

  if (!negotiationObj || !setNegotiationObj || !setNegotiationObjVersion) {
    logger.warn('Cannot update S3Files: negotiationObj or setters not available');
    return;
  }

  const taxYear = negotiationObj.TaxYear;
  if (!taxYear) {
    logger.warn('Cannot update S3Files: TaxYear not found in negotiationObj');
    return;
  }

  try {
    // Fetch S3Files for all parcels using the batch endpoint
    const response = await axiosInstanceNoToast.post('/get_s3_files_batch', {
      parcel_ids: parcelIds,
      tax_year: taxYear,
      bucket_name: 'aventine-court-docs'
    });

    let s3FilesMap = {};
    if (response.data.success && response.data.s3_files) {
      s3FilesMap = response.data.s3_files;
    } else {
      // If batch call fails, initialize empty map for all parcels
      parcelIds.forEach(parcelId => {
        s3FilesMap[parcelId] = [];
      });
      logger.warn('Batch S3 files fetch failed, using empty arrays:', response.data.error);
    }

    // Update negotiationObj with new S3Files
    const updatedNegotiationObj = JSON.parse(JSON.stringify(negotiationObj));
    
    parcelIds.forEach(parcelId => {
      const caseIndex = updatedNegotiationObj.cases.findIndex(c => c.parcel_id === parcelId);
      if (caseIndex !== -1) {
        // Merge new files with existing files, avoiding duplicates based on Key
        const existingFiles = updatedNegotiationObj.cases[caseIndex].S3Files || [];
        const newFiles = s3FilesMap[parcelId] || [];
        
        // Create a map of existing files by Key to avoid duplicates
        const existingFilesMap = new Map();
        existingFiles.forEach(file => {
          if (file.Key) {
            existingFilesMap.set(file.Key, file);
          }
        });
        
        // Add new files, replacing existing ones with same Key
        newFiles.forEach(file => {
          if (file.Key) {
            existingFilesMap.set(file.Key, file);
          }
        });
        
        // Convert back to array
        updatedNegotiationObj.cases[caseIndex].S3Files = Array.from(existingFilesMap.values());
      }
    });

    // Update the store
    setNegotiationObj(updatedNegotiationObj);
    setNegotiationObjVersion(prev => prev + 1);
    
    logger.log('✅ [WORKUP REGENERATION] Updated S3Files in negotiationObj for', parcelIds.length, 'parcels');
  } catch (error) {
    logger.error('Error updating S3Files in negotiationObj:', error);
    throw error;
  }
};
